import type { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { useAppDispatch } from 'Libs/redux/store';
import { redirect } from 'Libs/routing';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { createBreakingNews, type BreakingNewsArgs } from 'Store/entities/activity/activity.thunks';

import { BreakingNewsForm } from '../BreakingNewsForm/BreakingNewsForm';

const ALERT_DURATION = 5000;

export type DefaultValuesBreakingNewsForm = {
  name: string;
  selectedLanguageCode: LanguageCode | null;
};

export type UseBreakingNewsForm = (args: { onGoBack: () => void }) => {
  form: JSX.Element;
  canSubmit: boolean;
  submit: () => void;
  reset: () => void;
};

export const useBreakingNewsForm: UseBreakingNewsForm = ({ onGoBack }) => {
  const submitting = useRef(false);
  const dispatch = useAppDispatch();

  const { watch, setValue, reset, getValues } = useForm({
    defaultValues: {
      name: '',
      selectedLanguageCode: null,
    } as DefaultValuesBreakingNewsForm,
  });

  const submit = useCallback(async () => {
    if (submitting.current) return;

    const { name, selectedLanguageCode } = getValues();

    if (!selectedLanguageCode) return;

    const breakingNews: BreakingNewsArgs = {
      informations: {
        name: {
          [selectedLanguageCode]: name,
        },
      },
      languages: [selectedLanguageCode],
    };

    submitting.current = true;

    const { unwrap } = dispatch(createBreakingNews(breakingNews));

    try {
      const result = await unwrap();

      redirect(`/activity/breaking-news/${result.id}`);
    } catch (error) {
      console.error(error);
      enqueueBasicAlert(
        {
          id: 'breaking_news_form_creation_failed',
          status: 'error',
          title: t('activities:list.breaking_news_form.creation_failed'),
        },
        ALERT_DURATION,
      );
    } finally {
      submitting.current = false;
    }
  }, [dispatch, getValues]);

  const canSubmit = !!watch('name') && !!watch('selectedLanguageCode');

  return {
    form: (
      <BreakingNewsForm
        name={watch('name')}
        onNameChange={(value) => setValue('name', value)}
        selectedLanguageCode={watch('selectedLanguageCode')}
        onLanguageSelect={(value) => setValue('selectedLanguageCode', value)}
        onBack={onGoBack}
      />
    ),
    canSubmit,
    submit,
    reset,
  };
};
